import { EVENT_TYPES, TEventType, TFeatureSet, TPageTemplate } from 'Src/ng2/shared/typings/interfaces/mixpanel.interface';

/**
 * Values are sentence case because they are read directly in Mixpanel reports; the keys stay
 * SCREAMING_SNAKE for call sites. Safe to shape freely — `Search Action` appears in neither Lexicon,
 * so no released report is built on these strings.
 */
export enum SEARCH_ACTIONS {
  // Deliberately dormant: the global search box fired this on every settled keystroke pause, which
  // drowned the useful selection events in Mixpanel. The contract is kept so a future surface with a
  // lower-volume search can emit it without re-litigating the payload — it is not dead code.
  PERFORM_SEARCH = 'Perform search',
  SELECTED_STUDENT_RESULT = 'Selected student result',
  SELECTED_ENTITY_RESULT = 'Selected entity result',
}

export type TSearchAction = typeof SEARCH_ACTIONS[keyof typeof SEARCH_ACTIONS];

/** Where the search UI lives. One member today; app-wide search surfaces are added here. */
export const SearchSourceElement = {
  GlobalHeader: 'Global header',
} as const;

export type TSearchSourceElement = typeof SearchSourceElement[keyof typeof SearchSourceElement];

/** Where picking a result navigates to. Closed so destinations cannot fragment into near-synonyms. */
export const SearchDestinationPage = {
  Profile: 'Profile',
} as const;

export type TSearchDestinationPage = typeof SearchDestinationPage[keyof typeof SearchDestinationPage];

/**
 * The shared verb behind each search action. `action` is what reports aggregate on across surfaces;
 * `searchAction` carries the search-specific detail. Keeping the two apart is what stops this event's
 * vocabulary from fragmenting `action` the way the pass-rates filter enum did.
 */
export const SEARCH_ACTION_TO_EVENT_TYPE: Record<TSearchAction, TEventType> = {
  [SEARCH_ACTIONS.PERFORM_SEARCH]: EVENT_TYPES.SEARCHED,
  [SEARCH_ACTIONS.SELECTED_STUDENT_RESULT]: EVENT_TYPES.CLICKED,
  [SEARCH_ACTIONS.SELECTED_ENTITY_RESULT]: EVENT_TYPES.CLICKED,
};

export interface ISearchActionMetadata {
  action: TEventType;
  searchAction: TSearchAction;
  // Where the search happened, replacing the raw-URL `origin`. Optional because a page with no
  // classification entry reports nothing rather than a guess.
  featureSet?: TFeatureSet;
  pageTemplate?: TPageTemplate;
  // Length of the typed text, never the text itself — the box takes student names.
  // Emitted by nothing today; belongs to the dormant PERFORM_SEARCH action above.
  searchTextLength?: number;
  // How many results the typed text matched, at the moment the search settled. Also dormant.
  searchResultCount?: number;
  // The UX surface the search was launched from. Constant per surface, not per interaction.
  searchSourceElement?: TSearchSourceElement;
  // Only on the SELECTED_* actions — a search nobody picks a result from navigates nowhere.
  searchDestinationPage?: TSearchDestinationPage;
}

/**
 * The fields that vary per action. Derived from the contract above so the two cannot drift; the rest
 * of the payload is resolved centrally rather than passed by a call site.
 */
export type TSearchActionDetails = Pick<
  ISearchActionMetadata,
  'searchTextLength' | 'searchResultCount' | 'searchDestinationPage'
>;
